//IMPORTS

const { getRoleBaseDashboardPath, constructMicroAppUrl } = useNavigation();
/**
 * Map of allowed user roles for each path prefix
 */
const allowedUserRolesforPathPrefix = {
  'dashboard/teacher': ['teacher', 'admin'],
  'dashboard/learner': ['student'],
  'dashboard/parent': ['parent']
};
const devAllowedUserRolesforPathPrefix = {
  '/teacher': ['teacher', 'admin'],
  '/learner': ['student'],
  '/parent': ['parent']
};

/**
 * Middleware for authorisation based on user role
 * Check if the user is authorised to access the specified URL
 * 1. Learner is authorised to access the URLs which start with dashboard/learner
 * 2. Teacher is authorised to access the URLs which start with dashboard/teacher
 * 3. Parent is authorised to access the URLs which start with dashboard/parent
 *
 * If the user is not authorised to access the URL, redirect the user
 * to the appropriate dashboard depending on the user's role.
 * If the user's role is undefined or user is not authenticated,
 * redirect to the login page.
 */
export default defineNuxtRouteMiddleware(async (to) => {
  const isDevEnv = useRuntimeConfig().public.appEnv == 'local';
  const allowedRolePath = isDevEnv
    ? devAllowedUserRolesforPathPrefix
    : allowedUserRolesforPathPrefix;

  let userRole;
  if (import.meta.client) {
    // Extract the user data from the authenticate-data
    const { data } = useNuxtData('authenticate-data');
    // Load it into the Authenticate Store
    useAuthenticateStore().load(data.value);
    userRole = useAuthenticateStore().role;
  } else {
    // Load the user data from the session
    const event = useRequestEvent();
    userRole = event?.context.session?.role;
  }

  // Get the role based on the path
  const allowedUserRoleBasedOnPath =
    Object.entries(allowedRolePath).find(([path]) =>
      to.href.includes(path)
    )?.[1] || [];

  if (userRole && !allowedUserRoleBasedOnPath.includes(userRole)) {
    /**
     * If the user is not authorised to access the current URL,
     * redirect to the dashboard (based on the user role)
     */
    const redirectUrl = getRedirectionUrl(userRole);
    if (to.path !== redirectUrl) {
      return navigateTo(redirectUrl, { external: true });
    }
  }
});

/**
 * Generates a redirection URL based on the user's role.
 * Defaults to 'login' path if the role is not predefined.
 *
 * @param {string} userRole - User's role, expected to match a predefined role.
 * @returns {string} Redirection URL based on the role.
 */
const getRedirectionUrl = (userRole: string) => {
  const config = useRuntimeConfig().public.microapps.urlContracts;

  // Define the app names for each role
  const roleAppNames: { [key: string]: string } = {
    teacher: config.dashboard,
    student: config.dashboard,
    parent: config.dashboard,
    admin: config.admin,
    superadmin: config['support-admin'],
    default: config.onboarding
  };

  // Get the app name based on the user role
  const appName = roleAppNames[userRole] || roleAppNames.default;

  // Get the path based on the user role or default to 'login'
  const path = roleAppNames[userRole]
    ? getRoleBaseDashboardPath(userRole)
    : 'login';

  // Construct the redirection URL
  return constructMicroAppUrl([path], appName);
};
